/**
 * User photo submission API client.
 *
 * Talks to tschepp-ar-api. The endpoint is experimental: anonymous via
 * X-Device-Id header (a persistent random UUID stored in localStorage).
 */

const API_BASE = 'https://guide-api.arkserver.arkturian.com'

const DEVICE_ID_KEY = 'tschepp-device-id'

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) {
    id = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem(DEVICE_ID_KEY, id)
  }
  return id
}

export type SubmissionStatus = 'pending' | 'approved' | 'rejected' | 'error'
export type SubmissionType = 'photo' | 'video' | 'text'

export interface Submission {
  id: number
  submission_type: SubmissionType
  status: SubmissionStatus
  storage_id: number | null
  waypoint_id: number | null
  description: string
  latitude: number
  longitude: number
  snapped_latitude: number | null
  snapped_longitude: number | null
  distance_to_track_m: number | null
  route_id: number | null
  track_id: number | null
  rejection_reason: string | null
  error_reason: string | null
  created_at: string
  updated_at: string
}

export interface SubmissionListResponse {
  submissions: Submission[]
  total: number
}

export interface CreateSubmissionInput {
  submission_type: SubmissionType
  /** Required for photo + video, omitted for text */
  photo?: File
  description: string
  latitude: number
  longitude: number
  /** The track the submission belongs to. The API rejects the upload with
   *  `track_id required (no default configured)` without it (Alex 2026-07-23). */
  track_id: number
}

export async function createSubmission(input: CreateSubmissionInput): Promise<Submission> {
  const form = new FormData()
  form.append('submission_type', input.submission_type)
  if (input.photo) form.append('photo', input.photo)
  form.append('description', input.description)
  form.append('latitude', String(input.latitude))
  form.append('longitude', String(input.longitude))
  form.append('track_id', String(input.track_id))
  const res = await fetch(`${API_BASE}/api/v1/submissions`, {
    method: 'POST',
    headers: { 'X-Device-Id': getDeviceId() },
    body: form,
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`Upload failed (${res.status}): ${detail || res.statusText}`)
  }
  return res.json()
}

export async function getSubmission(id: number): Promise<Submission> {
  const res = await fetch(`${API_BASE}/api/v1/submissions/${id}`, {
    headers: { 'X-Device-Id': getDeviceId() },
  })
  if (!res.ok) throw new Error(`Fetch failed (${res.status})`)
  return res.json()
}

export async function listSubmissions(): Promise<SubmissionListResponse> {
  const res = await fetch(`${API_BASE}/api/v1/submissions`, {
    headers: { 'X-Device-Id': getDeviceId() },
  })
  if (!res.ok) throw new Error(`List failed (${res.status})`)
  return res.json()
}

/**
 * Direct media URL for a storage object — works once the submission has
 * status=approved (the storage object is permanent at that point).
 */
export function submissionMediaUrl(storageId: number, opts?: { variant?: 'thumbnail' | 'medium' | 'full' }): string {
  const v = opts?.variant || 'medium'
  return `https://api-storage.arkturian.com/storage/media/${storageId}?variant=${v}`
}

// ── Community „Aus der Schlucht" (#4555 §13) ────────────────────────────────

/** Vertrag GuideDevBot 2026-08-10: approved-Beiträge ALLER Geräte, ohne
 *  Koordinaten (bewusst — §6.1); Ortsbezug ist km_label/along_meters. */
export interface PublicSubmission {
  id: number
  submission_type: SubmissionType
  storage_id: number | null
  description: string
  created_at: string
  along_meters: number | null
  km_label: string | null
  distance_to_track_m: number | null
}

/** Fail-open: solange der Endpoint nicht deployt ist (404) oder das Netz
 *  fehlt, liefert dies [] — die Community-Sektion bleibt dann unsichtbar. */
export async function listPublicSubmissions(trackId: number, limit = 20): Promise<PublicSubmission[]> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/submissions/public?track_id=${trackId}&limit=${limit}`)
    if (!res.ok) return []
    const data = await res.json() as { submissions?: PublicSubmission[] }
    return data.submissions ?? []
  } catch {
    return []
  }
}

